import api from '../api/axios';
import type {
  DocumentNode,
  DocumentIndexState,
  DocumentSpace,
  ListDocumentNodesParams,
  PresignedDownload,
  PurgeJob,
  ReserveUploadBatchInput,
  ReservedUploadSession,
  UploadProgress,
  UploadSession,
} from '../types/documents';
import { normalizeDocumentCapabilities } from '../utils/documentCapabilities';

const DOCUMENTS_URL = 'api/documents';

export interface DocumentStoragePolicy {
  limit_bytes: number;
  used_bytes: number;
  reserved_bytes: number;
  available_bytes: number;
  max_file_bytes: number;
  max_batch_bytes: number;
  max_files_per_batch: number;
  chat_attachment_max_bytes: number;
}

export const getDocumentStoragePolicy = async (): Promise<DocumentStoragePolicy> => {
  const { data } = await api.get(`${DOCUMENTS_URL}/storage-usage/`);
  return data;
};

const normalizeDocumentSpace = (space: DocumentSpace): DocumentSpace => ({
  ...space,
  capabilities: normalizeDocumentCapabilities(space.capabilities),
});

export const listDocumentSpaces = async (): Promise<DocumentSpace[]> => {
  const { data } = await api.get(`${DOCUMENTS_URL}/spaces/`);
  return data.map(normalizeDocumentSpace);
};

export const getDocumentSpace = async (spaceId: number): Promise<DocumentSpace> => {
  const { data } = await api.get(`${DOCUMENTS_URL}/spaces/${spaceId}/`);
  return normalizeDocumentSpace(data);
};

export interface DocumentContactOption {
  id: number;
  name: string;
  contact_type: 'individual' | 'company';
  has_dossier: boolean;
  available: boolean;
}

export interface DocumentContactOptionsPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: DocumentContactOption[];
}

export const listDocumentContactOptions = async (
  search: string,
  offset = 0,
): Promise<DocumentContactOptionsPage> => {
  const { data } = await api.get(`${DOCUMENTS_URL}/spaces/contact-options/`, {
    params: { search, limit: 20, offset },
  });
  return data;
};

export const ensureContactDocumentSpace = async (contactId: number): Promise<DocumentSpace> => {
  const { data } = await api.post(`${DOCUMENTS_URL}/spaces/ensure-for-contact/`, {
    contact_id: contactId,
  });
  return normalizeDocumentSpace(data);
};

export const listDocumentNodes = async (
  spaceId: number,
  params: ListDocumentNodesParams = {},
): Promise<DocumentNode[]> => {
  const query: Record<string, string | number> = { space_id: spaceId };
  if (params.parentId !== undefined) query.parent_id = params.parentId === null ? 'null' : params.parentId;
  if (params.search) query.search = params.search;
  if (params.nodeType) query.node_type = params.nodeType;
  if (params.ordering) query.ordering = params.ordering;
  const { data } = await api.get(`${DOCUMENTS_URL}/nodes/`, { params: query });
  return data;
};

export const getDocumentNode = async (nodeId: number): Promise<DocumentNode> => {
  const { data } = await api.get(`${DOCUMENTS_URL}/nodes/${nodeId}/`);
  return data;
};

// Il backend non espone un comando di reindicizzazione: l'operazione pubblica
// disponibile è la lettura/polling del lifecycle sul dettaglio del nodo.
export const getDocumentIndexState = async (nodeId: number): Promise<DocumentIndexState> => {
  const node = await getDocumentNode(nodeId);
  return {
    nodeId: node.id,
    status: node.index_status,
    reasonCode: node.index_reason_code,
    reason: node.index_reason,
    updatedAt: node.index_updated_at,
    indexedAt: node.indexed_at,
  };
};

export const createDocumentFolder = async (
  spaceId: number,
  name: string,
  parentId: number | null = null,
): Promise<DocumentNode> => {
  const { data } = await api.post(`${DOCUMENTS_URL}/nodes/`, {
    space: spaceId,
    node_type: 'FOLDER',
    name,
    parent: parentId,
  });
  return data;
};

export const renameDocumentNode = async (nodeId: number, name: string): Promise<DocumentNode> => {
  const { data } = await api.patch(`${DOCUMENTS_URL}/nodes/${nodeId}/`, { name });
  return data;
};

export const moveDocumentNode = async (
  nodeId: number,
  parentId: number | null,
  destinationSpaceId?: number,
): Promise<DocumentNode> => {
  const payload: { parent: number | null; space?: number } = { parent: parentId };
  if (destinationSpaceId !== undefined) payload.space = destinationSpaceId;
  const { data } = await api.patch(`${DOCUMENTS_URL}/nodes/${nodeId}/`, payload);
  return data;
};

export const deleteDocumentNode = (nodeId: number): Promise<unknown> =>
  api.delete(`${DOCUMENTS_URL}/nodes/${nodeId}/`);

export const listTrashedDocumentNodes = async (spaceId: number, parentId?: number): Promise<DocumentNode[]> => {
  const params: { space_id: number; parent?: number } = { space_id: spaceId };
  if (parentId !== undefined) params.parent = parentId;
  const { data } = await api.get(`${DOCUMENTS_URL}/nodes/trash/`, { params });
  return data;
};

export const restoreDocumentNode = async (
  nodeId: number,
  targetParentId?: number | null,
): Promise<DocumentNode> => {
  const body = targetParentId === undefined ? {} : { target_parent_id: targetParentId };
  const { data } = await api.post(`${DOCUMENTS_URL}/nodes/${nodeId}/restore/`, body);
  return data;
};

export const purgeDocumentNode = async (nodeId: number): Promise<PurgeJob> => {
  const { data } = await api.delete(`${DOCUMENTS_URL}/nodes/${nodeId}/purge/`);
  return data;
};

export const downloadDocumentFile = (nodeId: number) =>
  api.get(`${DOCUMENTS_URL}/nodes/${nodeId}/download/`, { responseType: 'blob' });

export const getPresignedDocumentDownload = async (
  nodeId: number,
  disposition: 'inline' | 'attachment' = 'attachment',
): Promise<PresignedDownload> => {
  const { data } = await api.get(`${DOCUMENTS_URL}/nodes/${nodeId}/presign-download/`, {
    params: { disposition },
  });
  return data;
};

export const zipDocumentNodes = (nodeIds: number[]) =>
  api.post(`${DOCUMENTS_URL}/nodes/zip/`, { node_ids: nodeIds }, { responseType: 'blob' });

export const reserveUploadBatch = async (
  input: ReserveUploadBatchInput,
): Promise<ReservedUploadSession[]> => {
  const { data } = await api.post(
    `${DOCUMENTS_URL}/upload-sessions/`,
    {
      space_id: input.spaceId,
      parent_id: input.parentId ?? null,
      source_type: input.sourceType ?? 'manual_upload',
      files: input.files,
    },
    { headers: { 'Idempotency-Key': input.idempotencyKey } },
  );
  return data.files;
};

export const getUploadSession = async (uploadId: string): Promise<UploadSession> => {
  const { data } = await api.get(`${DOCUMENTS_URL}/upload-sessions/${uploadId}/`);
  return data;
};

export const cancelUploadSession = async (uploadId: string): Promise<UploadSession> => {
  const { data } = await api.delete(`${DOCUMENTS_URL}/upload-sessions/${uploadId}/`);
  return data;
};

export const completeUploadSession = async (uploadId: string): Promise<DocumentNode> => {
  const { data } = await api.post(`${DOCUMENTS_URL}/upload-sessions/${uploadId}/complete/`);
  return data;
};

// Upload diretto allo storage: non usa l'istanza API per evitare di inviare il
// Bearer token a un host S3. XHR espone progresso e abort al task coda upload.
export const uploadToPresignedPost = (
  session: ReservedUploadSession,
  file: File,
  options: { signal?: AbortSignal; onProgress?: (progress: UploadProgress) => void } = {},
): Promise<void> => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  const form = new FormData();
  Object.entries(session.presigned_post.fields).forEach(([key, value]) => form.append(key, value));
  form.append('file', file);

  xhr.open('POST', session.presigned_post.url);
  xhr.upload.onprogress = (event) => {
    const total = event.lengthComputable ? event.total : file.size;
    options.onProgress?.({
      loaded: event.loaded,
      total,
      percentage: total > 0 ? Math.round((event.loaded / total) * 100) : 0,
    });
  };
  xhr.onload = () => {
    if (xhr.status >= 200 && xhr.status < 300) resolve();
    else reject(new Error(`Presigned upload failed with status ${xhr.status}`));
  };
  xhr.onerror = () => reject(new Error('Presigned upload failed due to a network error'));
  xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));

  if (options.signal?.aborted) {
    reject(new DOMException('Upload aborted', 'AbortError'));
    return;
  }
  options.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
  xhr.send(form);
});
